import type { Range } from '@tiptap/core';
import type { MentionNodeAttrs } from '@tiptap/extension-mention';
import type { EditorView } from '@tiptap/pm/view';
import type { Editor } from '@tiptap/react';
import type { CSSProperties, Ref } from 'react';
import type { Instance as TippyInstance } from 'tippy.js';
import Link from '@tiptap/extension-link';
import Mention from '@tiptap/extension-mention';
import Placeholder from '@tiptap/extension-placeholder';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { EditorContent, Extension, ReactRenderer, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useRef, useState } from 'react';
import tippy from 'tippy.js';

import type { ColorName, ColorVariant, Style } from './buttonClasses';
import type { Shape, Size } from './types';
import { CDNImage } from '../CDNImage';
import { cn } from '../cn';
import { usePlatformImageCdn } from '../contexts';
import { Communication, Files } from '../StrokeIcons';
import Button from './Button';
import { composeVariant, inputVariantClasses } from './buttonClasses';
import { shapeRounded, sizeFont, sizeMinHeight, sizePaddingL, sizePaddingR } from './types';

const TRIGGER_HINT_IDLE_MS = 1200;

// Placeholder colors matching inputVariantPlaceholder from Input2
function getPlaceholderColors(variant: ColorVariant, error: boolean): [string, string] {
  if (error) return ['rgb(248 113 113)', 'rgb(127 29 29)']; // fair-pink-400, fair-pink-700

  const colors: Record<ColorVariant, [string, string]> = {
    blue: ['rgb(255 255 255)', 'rgb(255 255 255)'],
    red: ['rgb(255 255 255)', 'rgb(255 255 255)'],
    green: ['rgb(255 255 255)', 'rgb(255 255 255)'],
    yellow: ['rgb(133 77 14)', 'rgb(133 77 14)'],
    purple: ['rgb(255 255 255)', 'rgb(255 255 255)'],
    aurora: ['rgb(255 255 255)', 'rgb(255 255 255)'],
    auroraOutline: ['rgb(216 180 254)', 'rgb(107 33 168)'],
    auroraSoft: ['rgb(216 180 254)', 'rgb(107 33 168)'],
    blueOutline: ['rgb(147 197 253)', 'rgb(30 64 175)'],
    redOutline: ['rgb(248 113 113)', 'rgb(127 29 29)'],
    greenOutline: ['rgb(134 239 172)', 'rgb(22 101 52)'],
    yellowOutline: ['rgb(253 230 138)', 'rgb(133 77 14)'],
    purpleOutline: ['rgb(216 180 254)', 'rgb(107 33 168)'],
    blueSoft: ['rgb(147 197 253)', 'rgb(30 64 175)'],
    redSoft: ['rgb(248 113 113)', 'rgb(127 29 29)'],
    greenSoft: ['rgb(134 239 172)', 'rgb(22 101 52)'],
    yellowSoft: ['rgb(253 230 138)', 'rgb(133 77 14)'],
    purpleSoft: ['rgb(216 180 254)', 'rgb(107 33 168)'],
    forest: ['rgb(255 255 255)', 'rgb(255 255 255)'],
    forestOutline: ['rgb(126 186 142)', 'rgb(45 94 63)'], // forest-300, forest-600
    forestSoft: ['rgb(126 186 142)', 'rgb(45 94 63)'],
    cream: ['rgb(102 83 58)', 'rgb(102 83 58)'], // cream-800
    creamOutline: ['rgb(201 183 147)', 'rgb(157 133 90)'], // cream-400, cream-600
    creamSoft: ['rgb(201 183 147)', 'rgb(157 133 90)'],
    black: ['rgb(255 255 255)', 'rgb(255 255 255)'],
    blackOutline: ['rgb(163 163 163)', 'rgb(82 82 82)'], // coal-300, coal-500
    blackSoft: ['rgb(163 163 163)', 'rgb(82 82 82)'],
    white: ['rgb(161 161 170)', 'rgb(161 161 170)'], // paper-500
    whiteOutline: ['rgb(161 161 170)', 'rgb(161 161 170)'],
    whiteSoft: ['rgb(161 161 170)', 'rgb(161 161 170)'],
    gray: ['rgb(148 163 184)', 'rgb(71 85 105)'], // slate-400, slate-600
    grayOutline: ['rgb(148 163 184)', 'rgb(71 85 105)'],
    graySoft: ['rgb(148 163 184)', 'rgb(71 85 105)'],
    // `underline` tone reuses the outline placeholder colors.
    blueUnderline: ['rgb(147 197 253)', 'rgb(30 64 175)'],
    redUnderline: ['rgb(248 113 113)', 'rgb(127 29 29)'],
    greenUnderline: ['rgb(134 239 172)', 'rgb(22 101 52)'],
    yellowUnderline: ['rgb(253 230 138)', 'rgb(133 77 14)'],
    purpleUnderline: ['rgb(216 180 254)', 'rgb(107 33 168)'],
    auroraUnderline: ['rgb(216 180 254)', 'rgb(107 33 168)'],
    forestUnderline: ['rgb(126 186 142)', 'rgb(45 94 63)'],
    creamUnderline: ['rgb(201 183 147)', 'rgb(157 133 90)'],
    blackUnderline: ['rgb(163 163 163)', 'rgb(82 82 82)'],
    whiteUnderline: ['rgb(161 161 170)', 'rgb(161 161 170)'],
    grayUnderline: ['rgb(148 163 184)', 'rgb(71 85 105)'],
  };

  return colors[variant] ?? colors.gray;
}

/** Variable item for @ mentions */
export type TipTapVariable = {
  id: string;
  /**
   * The chip display text — what the editor renders inside the mention
   * span (typically `@VariableName`). This is what survives a save /
   * reload roundtrip via the span's `data-label` attribute.
   */
  label: string;
  /**
   * Optional resolved value for the current contact, shown as a small
   * secondary line in the suggestion dropdown so the user can see what
   * each variable will resolve to. Distinct from `label` so the chip
   * stays as the variable NAME instead of the resolved VALUE.
   */
  previewValue?: string;
  /** Optional group: 'user' for My Variables, 'contact' for Contact Variables */
  group?: 'user' | 'contact';
};

/** Document item for / mentions */
export type TipTapDocument = {
  id: string;
  label: string;
  /** Optional image preview URL */
  imageUrl?: string;
};

export type TipTapInputProps = {
  // Input2 styling props
  size?: Size;
  /** Color axis — `'blue' | 'red' | 'green' | …`. Defaults to `'gray'`. */
  color?: ColorName;
  /** Style axis — `'filled' | 'outline' | 'soft' | 'underline'`. Defaults to `'filled'`. */
  tone?: Style;
  shape?: Shape;
  bordered?: boolean;

  // Core editor props
  error?: string;
  onChange?: (html: string, text: string) => void;
  value?: string;
  placeholder?: string;
  className?: string;
  editorClassName?: string;
  errorClassName?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  autoFocus?: boolean;
  style?: CSSProperties;
  allowLinks?: boolean;
  allowFormatting?: boolean;
  onSubmit?: () => void;
  disabled?: boolean;
  readOnly?: boolean;

  // Mention props
  /** Variables for @ mentions */
  variables?: TipTapVariable[];
  /** Documents for / mentions */
  documents?: TipTapDocument[];
  /** Enable { comment mentions */
  showComments?: boolean;
  /** Disable all mention triggers */
  disableMentions?: boolean;
  /** Minimum number of rows (affects min-height) */
  rows?: number;
  /** Callback when user clicks "Create new document" in the / dropdown */
  onCreateDocument?: () => void;
};

// Render the mention label in the editor
function renderLabel({ node }: { node: { attrs: { id?: string; label?: string } } }): string {
  const { id, label } = node.attrs;
  if (id?.startsWith('{') && id?.endsWith('}')) {
    return id; // Comment format
  }
  return label ?? id ?? '';
}

// Suggestion `command` that inserts the mention node in place of the typed trigger WITHOUT the trailing
// space @tiptap/extension-mention appends by default (its built-in command inserts `[mention, ' ']`,
// which lands in the saved HTML). `nodeName` must match the extended node's name (atVariables /
// documents / comments). Shared by all three mentions so they insert consistently.
function insertMentionWithoutTrailingSpace(nodeName: string) {
  return ({ editor, range, props }: { editor: Editor; range: Range; props: MentionNodeAttrs }) => {
    editor.chain().focus().insertContentAt(range, { type: nodeName, attrs: props }).run();
    // Matches the default command's Firefox cursor fix after inserting the node.
    window.getSelection()?.collapseToEnd();
  };
}

// Submit extension for Cmd+Enter
const SubmitExtension = (uniqFieldId: string, onSubmit?: () => void) =>
  Extension.create({
    addKeyboardShortcuts() {
      return {
        'Mod-Enter'() {
          if (onSubmit) {
            onSubmit();
            return true;
          }
          const input = document.querySelector<HTMLInputElement>(`input[data-editor-id="${uniqFieldId}"]`);
          input?.click();
          return true;
        },
      };
    },
  });

// ─────────────────────────────────────────────────────────────────────────────
// Mention suggestion list component - styled like FilterPopover
// ─────────────────────────────────────────────────────────────────────────────

type MentionListProps = {
  items: Array<{ id: string; label: string; group?: string }>;
  command: (item: { id: string; label: string }) => void;
};

type MentionListRef = {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
};

/**
 * Case-insensitive filter for the @ variable picker. Matches against both
 * the variable name (id, e.g. "MyName") and the resolved value
 * (label, e.g. "Michael"). Exported for unit testing.
 */
export function filterVariablesByQuery<T extends { id: string; label: string; previewValue?: string }>(
  items: T[],
  query: string,
): T[] {
  const q = query.toLowerCase();
  return items.filter(
    (item) =>
      item.id.toLowerCase().includes(q) ||
      item.label.toLowerCase().includes(q) ||
      (item.previewValue ?? '').toLowerCase().includes(q),
  );
}

// forwardRef required: @tiptap/react v2's ReactRenderer only forwards refs to forwardRef/class
// components, not React-19 ref-as-prop function components. This dropdown exposes its keyboard
// handler (Enter / Arrow keys) via that ref, so dropping forwardRef silently breaks selection.
// Remove on the TipTap v3 upgrade (v3 supports ref-as-prop). See tiptap#6110.
export const MentionList = forwardRef<MentionListRef, MentionListProps>(({ items, command }, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const selectItem = useCallback(
    (index: number) => {
      const item = items[index];
      if (item) command(item);
    },
    [items, command],
  );

  const scrollToIndex = useCallback((index: number) => {
    const el = listRef.current?.querySelector(`[data-option-index="${index}"]`) as HTMLElement | undefined;
    if (!el || !listRef.current) return;

    const container = listRef.current;
    const elRect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const stickyHeaderOffset = 28; // Account for sticky headers

    const hiddenAbove = containerRect.top + stickyHeaderOffset - elRect.top;
    const hiddenBelow = elRect.bottom - containerRect.bottom;

    if (hiddenAbove > 0) {
      container.scrollTop -= hiddenAbove;
    } else if (hiddenBelow > 0) {
      container.scrollTop += hiddenBelow;
    }
  }, []);

  useEffect(() => {
    setSelectedIndex(0);
  }, [items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === 'ArrowUp') {
        const next = selectedIndex > 0 ? selectedIndex - 1 : items.length - 1;
        setSelectedIndex(next);
        scrollToIndex(next);
        return true;
      }
      if (event.key === 'ArrowDown') {
        const next = selectedIndex < items.length - 1 ? selectedIndex + 1 : 0;
        setSelectedIndex(next);
        scrollToIndex(next);
        return true;
      }
      if (event.key === 'Enter') {
        selectItem(selectedIndex);
        return true;
      }
      return false;
    },
  }));

  if (items.length === 0) {
    return (
      <div className="bg-white dark:bg-charade-950 border border-slate-200 dark:border-charade-700 rounded-lg shadow-lg p-0">
        <div className="h-9 px-3 flex items-center text-sm text-slate-500 dark:text-slate-300">No results</div>
      </div>
    );
  }

  // Group items by their group property
  const userItems = items.filter((item) => item.group === 'user');
  const contactItems = items.filter((item) => item.group === 'contact');
  const otherItems = items.filter((item) => !item.group);
  const hasGroups = userItems.length > 0 || contactItems.length > 0;

  // Build flat list with group info for rendering
  const renderItems: Array<
    { type: 'header'; label: string } | { type: 'item'; item: (typeof items)[0]; flatIndex: number }
  > = [];
  let flatIndex = 0;

  if (hasGroups) {
    // Contact variables come first — they're the most commonly used per-step.
    if (contactItems.length > 0) {
      renderItems.push({ type: 'header', label: 'Contact Variables' });
      for (const item of contactItems) {
        renderItems.push({ type: 'item', item, flatIndex });
        flatIndex++;
      }
    }
    if (userItems.length > 0) {
      renderItems.push({ type: 'header', label: 'My Variables' });
      for (const item of userItems) {
        renderItems.push({ type: 'item', item, flatIndex });
        flatIndex++;
      }
    }
    if (otherItems.length > 0) {
      renderItems.push({ type: 'header', label: 'Other' });
      for (const item of otherItems) {
        renderItems.push({ type: 'item', item, flatIndex });
        flatIndex++;
      }
    }
  } else {
    for (const item of items) {
      renderItems.push({ type: 'item', item, flatIndex });
      flatIndex++;
    }
  }

  return (
    <div className="bg-white dark:bg-charade-950 border border-slate-200 dark:border-charade-700 rounded-lg shadow-lg p-0 min-w-[200px] overflow-hidden">
      <div ref={listRef} className="max-h-[40vh] overflow-auto">
        {renderItems.map((renderItem) => {
          if (renderItem.type === 'header') {
            return (
              <div
                key={`header-${renderItem.label}`}
                className="px-3 py-1.5 text-xs font-bold uppercase text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-charade-800 sticky top-0 z-10">
                {renderItem.label}
              </div>
            );
          }
          const { item, flatIndex: itemFlatIndex } = renderItem;
          // The TipTapVariable.id is the variable KEY (e.g. "FirstName"),
          // .label is the chip display text (e.g. "@FirstName"), and
          // .previewValue (when present) is what the variable will resolve
          // to for the current contact (e.g. "Michael"). Show the chip
          // label as the prominent line, the resolved value underneath
          // as a hint.
          const primary = item.label;
          const preview =
            (item as { previewValue?: string }).previewValue ?? (item.id !== item.label ? `@${item.id}` : null);
          const isSelected = itemFlatIndex === selectedIndex;
          return (
            <div
              key={item.id}
              role="option"
              data-option-index={itemFlatIndex}
              aria-selected={isSelected}
              onMouseDown={(e) => {
                e.preventDefault();
                selectItem(itemFlatIndex);
              }}
              onMouseEnter={() => setSelectedIndex(itemFlatIndex)}
              className={cn(
                'min-h-9 px-3 py-1.5 flex flex-col justify-center text-sm cursor-pointer',
                // Default text colour is set on the row so the primary
                // <span> below doesn't inherit the editor's surrounding
                // colour (which is illegibly dark against `charade-950`
                // on the dark contact-detail page).
                isSelected ? 'bg-astronaut-600 text-white' : 'text-slate-900 dark:text-slate-100',
                !isSelected && itemFlatIndex % 2 === 1 && 'bg-slate-50 dark:bg-charade-900',
                // Hover bumps to charade-800 so the hover state is
                // distinct from the zebra-striped (charade-900) odd
                // rows in dark mode.
                !isSelected && 'hover:bg-slate-100 dark:hover:bg-charade-800',
              )}>
              <span className="truncate font-medium">{primary}</span>
              {preview && (
                <span
                  className={cn(
                    'truncate text-xs',
                    // Full white on the highlighted (astronaut-600) row so the
                    // secondary @id line still meets AA — text-white/80 came
                    // out at ~3.86:1, below the 4.5 threshold.
                    isSelected ? 'text-white' : 'text-slate-500 dark:text-slate-300',
                  )}>
                  {preview}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// Document list component with image previews
// ─────────────────────────────────────────────────────────────────────────────

type DocumentListProps = {
  items: Array<{ id: string; label: string; imageUrl?: string }>;
  command: (item: { id: string; label: string }) => void;
  onCreateDocument?: () => void;
};

type DocumentListRef = {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
};

// forwardRef required — same constraint as MentionList above (TipTap v2 ReactRenderer only
// forwards refs to forwardRef/class components). Remove on the TipTap v3 upgrade. See tiptap#6110.
export const DocumentList = forwardRef<DocumentListRef, DocumentListProps>(
  ({ items, command, onCreateDocument }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const listRef = useRef<HTMLDivElement>(null);
    const imageCdn = usePlatformImageCdn();

    const selectItem = useCallback(
      (index: number) => {
        const item = items[index];
        if (item) command(item);
      },
      [items, command],
    );

    const scrollToIndex = useCallback((index: number) => {
      const el = listRef.current?.querySelector(`[data-option-index="${index}"]`) as HTMLElement | undefined;
      if (!el || !listRef.current) return;

      const container = listRef.current;
      const elRect = el.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      const hiddenAbove = containerRect.top - elRect.top;
      const hiddenBelow = elRect.bottom - containerRect.bottom;

      if (hiddenAbove > 0) {
        container.scrollTop -= hiddenAbove;
      } else if (hiddenBelow > 0) {
        container.scrollTop += hiddenBelow;
      }
    }, []);

    useEffect(() => {
      setSelectedIndex(0);
    }, [items]);

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (event.key === 'ArrowUp') {
          const next = selectedIndex > 0 ? selectedIndex - 1 : items.length - 1;
          setSelectedIndex(next);
          scrollToIndex(next);
          return true;
        }
        if (event.key === 'ArrowDown') {
          const next = selectedIndex < items.length - 1 ? selectedIndex + 1 : 0;
          setSelectedIndex(next);
          scrollToIndex(next);
          return true;
        }
        if (event.key === 'Enter') {
          selectItem(selectedIndex);
          return true;
        }
        return false;
      },
    }));

    // "Create new document" row component
    const createNewRow = onCreateDocument && (
      <div
        onMouseDown={(e) => {
          e.preventDefault();
          onCreateDocument();
        }}
        className="px-3 py-2 flex items-center gap-3 text-sm cursor-pointer border-t border-slate-200 dark:border-charade-700 hover:bg-slate-100 dark:hover:bg-charade-900 text-astronaut-600 dark:text-astronaut-400">
        <div className="w-12 h-8 rounded-sm border border-dashed border-astronaut-400 dark:border-astronaut-600 bg-astronaut-50 dark:bg-astronaut-900/20 shrink-0 flex items-center justify-center">
          <Files.FilePlus02 className="size-4" />
        </div>
        <span>Create new document</span>
      </div>
    );

    if (items.length === 0) {
      return (
        <div className="bg-white dark:bg-charade-950 border border-slate-200 dark:border-charade-700 rounded-lg shadow-lg p-0 overflow-hidden">
          {onCreateDocument && createNewRow}
          {!onCreateDocument && (
            <div className="h-9 px-3 flex items-center text-sm text-slate-400 dark:text-slate-600">No documents</div>
          )}
        </div>
      );
    }

    return (
      <div className="bg-white dark:bg-charade-950 border border-slate-200 dark:border-charade-700 rounded-lg shadow-lg p-0 min-w-[250px] overflow-hidden">
        <div ref={listRef} className="max-h-[40vh] overflow-auto">
          {items.map((item, index) => (
            <div
              key={item.id}
              role="option"
              data-option-index={index}
              aria-selected={index === selectedIndex}
              onMouseDown={(e) => {
                e.preventDefault();
                selectItem(index);
              }}
              onMouseEnter={() => setSelectedIndex(index)}
              className={cn(
                'px-3 py-2 flex items-center gap-3 text-sm cursor-pointer',
                index === selectedIndex && 'bg-astronaut-600 text-white',
                index !== selectedIndex && index % 2 === 1 && 'bg-slate-50 dark:bg-charade-900',
                index !== selectedIndex && 'hover:bg-slate-100 dark:hover:bg-charade-900',
              )}>
              {item.imageUrl && (
                <CDNImage
                  src={item.imageUrl}
                  alt=""
                  width={48}
                  height={32}
                  cdnWidth={96}
                  cdnBaseUrl={imageCdn?.cdnBaseUrl}
                  backendUrl={imageCdn?.backendUrl}
                  className="w-12 h-8 object-cover rounded-sm border border-slate-200 dark:border-charade-700 shrink-0"
                />
              )}
              {!item.imageUrl && (
                <div className="w-12 h-8 rounded-sm border border-slate-200 dark:border-charade-700 bg-slate-100 dark:bg-charade-800 shrink-0 flex items-center justify-center">
                  <span className="text-xs text-slate-400">Doc</span>
                </div>
              )}
              <span className="truncate flex-1">{item.label}</span>
            </div>
          ))}
        </div>
        {createNewRow}
      </div>
    );
  },
);

// ─────────────────────────────────────────────────────────────────────────────
// Comment form component for { mentions
// ─────────────────────────────────────────────────────────────────────────────

type CommentFormProps = {
  command: (item: { id: string; label: string }) => void;
};

type CommentFormRef = {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
};

// forwardRef required — same constraint as MentionList above (TipTap v2 ReactRenderer only
// forwards refs to forwardRef/class components). Remove on the TipTap v3 upgrade. See tiptap#6110.
const CommentForm = forwardRef<CommentFormRef, CommentFormProps>(({ command }, ref) => {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const submitComment = useCallback(() => {
    if (!value.trim()) return;
    // Insert as {value} format
    command({ id: `{${value}}`, label: value });
  }, [value, command]);

  useEffect(() => {
    // Auto-focus the input. Defer to the next frame: ReactRenderer commits this
    // component into a *detached* element that Tippy only appends + shows on the
    // same tick, so focusing synchronously on mount targets a not-yet-visible
    // input and the browser silently drops the focus. By the next frame the
    // popup is attached and painted, so the focus sticks.
    const raf = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, []);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        submitComment();
        return true;
      }
      return false;
    },
  }));

  return (
    <div className="bg-white dark:bg-charade-950 border border-slate-200 dark:border-charade-700 rounded-lg shadow-lg p-2 min-w-[250px]">
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submitComment();
            }
          }}
          placeholder="Type comment text..."
          className="flex-1 h-9 px-3 text-sm bg-slate-50 dark:bg-charade-900 border border-slate-200 dark:border-charade-700 rounded-md outline-hidden focus:border-astronaut-500 dark:focus:border-astronaut-400"
        />
        <button
          type="button"
          onClick={submitComment}
          disabled={!value.trim()}
          className={cn(
            'h-9 w-9 flex items-center justify-center rounded-md transition-colors',
            value.trim()
              ? 'bg-astronaut-600 text-white hover:bg-astronaut-700'
              : 'bg-slate-100 dark:bg-charade-800 text-slate-400 cursor-not-allowed',
          )}>
          <Communication.Send03 className="size-4" />
        </button>
      </div>
      <p className="mt-1.5 text-xs text-slate-400 dark:text-slate-500">Press Enter to insert comment</p>
    </div>
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// Trigger hint popup — idle "/" and "@" discovery affordance
// ─────────────────────────────────────────────────────────────────────────────

export type TriggerInfo = { char: string; label: string; tooltip: string };

export type TriggerHintPopupProps = {
  triggers: TriggerInfo[];
  onInsert: (char: string) => void;
};

export function TriggerHintPopup({ triggers, onInsert }: TriggerHintPopupProps) {
  return (
    <div
      data-testid="tiptap-trigger-hint"
      onMouseDown={(e) => e.preventDefault()}
      className="flex gap-1 bg-white dark:bg-charade-950 border border-slate-200 dark:border-charade-700 rounded-lg shadow-lg p-1">
      {triggers.map((t) => (
        <Button key={t.char} size="sm" color="blue" tone="soft" tooltip={t.tooltip} onClick={() => onInsert(t.char)}>
          <span className="font-mono mr-1">{t.char}</span>
          {t.label}
        </Button>
      ))}
    </div>
  );
}

// Detect whether one of the existing Suggestion plugins (@, /, {) is currently
// showing its picker. We combine two signals so the hint never overlaps a
// real suggestion: (a) any *other* visible tippy box in the document — the
// Mention plugins each mount their dropdown via tippy and the hint marks its
// own tippy with `tippy-trigger-hint` so we can exclude it; (b) a regex on
// the text immediately before the cursor catching the brief window between
// typing `@`/`/`/`{` and the dropdown appearing on screen.
const TRIGGER_HINT_TIPPY_CLASS = 'tippy-trigger-hint';

function isSuggestionActiveAtCursor(view: EditorView): boolean {
  const otherTippy = document.querySelector(`.tippy-box[data-state="visible"]:not(.${TRIGGER_HINT_TIPPY_CLASS})`);
  if (otherTippy) return true;
  const { state } = view;
  const $head = state.selection.$head;
  const from = Math.max(0, $head.parentOffset - 200);
  const textBefore = $head.parent.textBetween(from, $head.parentOffset, undefined, '￼');
  if (/(?:^|\s)[@/]\S*$/.test(textBefore)) return true;
  if (/(?:^|\s)\{[^}]*$/.test(textBefore)) return true;
  return false;
}

function createTriggerHintExtension(triggers: TriggerInfo[]) {
  const key = new PluginKey('triggerHint');
  return Extension.create({
    name: 'triggerHint',
    addProseMirrorPlugins() {
      const editor = this.editor;
      return [
        new Plugin({
          key,
          view(view) {
            let timer: ReturnType<typeof setTimeout> | null = null;
            let component: ReactRenderer<unknown, TriggerHintPopupProps> | null = null;
            let popup: TippyInstance[] | null = null;
            let visible = false;

            const clearTimer = () => {
              if (timer) {
                clearTimeout(timer);
                timer = null;
              }
            };

            const hide = () => {
              clearTimer();
              if (visible) {
                popup?.[0]?.hide();
                visible = false;
              }
            };

            const insert = (ch: string) => {
              hide();
              editor.chain().focus().insertContent(ch).run();
            };

            const show = () => {
              if (!view.hasFocus()) return;
              const { state } = view;
              if (!state.selection.empty) return;
              if ((view as unknown as { composing?: boolean }).composing) return;
              if (isSuggestionActiveAtCursor(view)) return;

              const coords = view.coordsAtPos(state.selection.head);
              const getRect = (): DOMRect =>
                ({
                  top: coords.bottom,
                  bottom: coords.bottom,
                  left: coords.left,
                  right: coords.left,
                  width: 0,
                  height: 0,
                  x: coords.left,
                  y: coords.bottom,
                  toJSON: () => ({}),
                }) as DOMRect;

              if (!component) {
                component = new ReactRenderer(TriggerHintPopup, {
                  props: { triggers, onInsert: insert },
                  editor,
                });
              }
              if (!popup) {
                popup = tippy('body', {
                  getReferenceClientRect: getRect,
                  appendTo: () => document.body,
                  content: component.element,
                  showOnCreate: false,
                  interactive: true,
                  trigger: 'manual',
                  placement: 'bottom-start',
                  offset: [0, 4],
                  hideOnClick: false,
                  onCreate(instance) {
                    instance.popper.querySelector('.tippy-box')?.classList.add(TRIGGER_HINT_TIPPY_CLASS);
                  },
                });
              } else {
                popup[0]?.setProps({ getReferenceClientRect: getRect });
              }
              popup[0]?.show();
              visible = true;
            };

            const schedule = () => {
              hide();
              timer = setTimeout(show, TRIGGER_HINT_IDLE_MS);
            };

            const onFocus = () => schedule();
            const onBlur = () => hide();

            view.dom.addEventListener('focus', onFocus);
            view.dom.addEventListener('blur', onBlur);
            if (view.hasFocus()) schedule();

            return {
              update(_view, prevState) {
                if (!view.hasFocus()) {
                  hide();
                  return;
                }
                const docChanged = !prevState.doc.eq(view.state.doc);
                const selChanged = !prevState.selection.eq(view.state.selection);
                if (docChanged || selChanged) schedule();
              },
              destroy() {
                view.dom.removeEventListener('focus', onFocus);
                view.dom.removeEventListener('blur-sm', onBlur);
                hide();
                popup?.[0]?.destroy();
                popup = null;
                component?.destroy();
                component = null;
              },
            };
          },
        }),
      ];
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Main TipTapInput component
// ─────────────────────────────────────────────────────────────────────────────

const TipTapInput = ({
  size = 'md',
  color,
  tone,
  shape,
  bordered = true,
  error,
  onChange,
  value,
  placeholder,
  className,
  editorClassName,
  errorClassName,
  onFocus,
  onBlur,
  autoFocus,
  style,
  allowLinks,
  allowFormatting,
  onSubmit,
  disabled = false,
  readOnly = false,
  variables = [],
  documents = [],
  showComments = false,
  disableMentions = false,
  rows = 1,
  onCreateDocument,
  ref,
}: TipTapInputProps & { ref?: Ref<Editor> }) => {
  const [isMounted, setIsMounted] = useState(false);
  const [wasFocused, setWasFocused] = useState(false);
  // The editor's last-emitted HTML, so the value->editor sync effect can detect
  // a genuine external change without re-serializing getHTML() on every render.
  const lastHtmlRef = useRef<string | undefined>(value);
  const uniqFieldId = useId();

  // Create suggestion handler for @ variable mentions
  const createVariableSuggestion = useCallback(
    (items: Array<{ id: string; label: string; group?: string }>) => ({
      char: '@',
      pluginKey: new PluginKey('mention @'),
      command: insertMentionWithoutTrailingSpace('atVariables'),
      // Case-insensitive substring match on both id (variable name) and
      // label (resolved value). No total-count slice — dropdown scrolls.
      items: ({ query }: { query: string }) => filterVariablesByQuery(items, query),
      render: () => {
        let component: ReactRenderer | null = null;
        let popup: TippyInstance[] | null = null;

        return {
          onStart: (props: {
            editor: Editor;
            clientRect?: (() => DOMRect | null) | null;
            command: (item: { id: string; label: string }) => void;
          }) => {
            component = new ReactRenderer(MentionList, {
              props,
              editor: props.editor,
            });

            if (!props.clientRect) return;

            popup = tippy('body', {
              getReferenceClientRect: props.clientRect as () => DOMRect,
              appendTo: () => document.body,
              content: component.element,
              showOnCreate: true,
              interactive: true,
              trigger: 'manual',
              placement: 'bottom-start',
            });
          },
          onUpdate: (props: { clientRect?: (() => DOMRect | null) | null }) => {
            component?.updateProps(props);
            if (!props.clientRect) return;
            popup?.[0]?.setProps({ getReferenceClientRect: props.clientRect as () => DOMRect });
          },
          onKeyDown: (props: { event: KeyboardEvent }) => {
            if (props.event.key === 'Escape') {
              popup?.[0]?.hide();
              return true;
            }
            return (
              (component?.ref as { onKeyDown?: (props: { event: KeyboardEvent }) => boolean })?.onKeyDown?.(props) ??
              false
            );
          },
          onExit: () => {
            popup?.[0]?.destroy();
            component?.destroy();
          },
        };
      },
    }),
    [],
  );

  // Create suggestion handler for / document mentions (with image preview)
  const createDocumentSuggestion = useCallback(
    (items: TipTapDocument[], onCreateDoc?: () => void) => ({
      char: '/',
      pluginKey: new PluginKey('mention /'),
      command: insertMentionWithoutTrailingSpace('documents'),
      items: ({ query }: { query: string }) => {
        const q = query.toLowerCase();
        return items.filter((item) => item.label.toLowerCase().includes(q));
      },
      render: () => {
        let component: ReactRenderer | null = null;
        let popup: TippyInstance[] | null = null;

        // Wrap onCreateDocument to also close the popup
        const handleCreateDocument = onCreateDoc
          ? () => {
              popup?.[0]?.hide();
              onCreateDoc();
            }
          : undefined;

        return {
          onStart: (props: {
            editor: Editor;
            clientRect?: (() => DOMRect | null) | null;
            command: (item: { id: string; label: string }) => void;
          }) => {
            component = new ReactRenderer(DocumentList, {
              props: { ...props, onCreateDocument: handleCreateDocument },
              editor: props.editor,
            });

            if (!props.clientRect) return;

            popup = tippy('body', {
              getReferenceClientRect: props.clientRect as () => DOMRect,
              appendTo: () => document.body,
              content: component.element,
              showOnCreate: true,
              interactive: true,
              trigger: 'manual',
              placement: 'bottom-start',
            });
          },
          onUpdate: (props: { clientRect?: (() => DOMRect | null) | null }) => {
            component?.updateProps(props);
            if (!props.clientRect) return;
            popup?.[0]?.setProps({ getReferenceClientRect: props.clientRect as () => DOMRect });
          },
          onKeyDown: (props: { event: KeyboardEvent }) => {
            if (props.event.key === 'Escape') {
              popup?.[0]?.hide();
              return true;
            }
            return (
              (component?.ref as { onKeyDown?: (props: { event: KeyboardEvent }) => boolean })?.onKeyDown?.(props) ??
              false
            );
          },
          onExit: () => {
            popup?.[0]?.destroy();
            component?.destroy();
          },
        };
      },
    }),
    [],
  );

  // Create suggestion handler for { comment mentions (shows form)
  const createCommentSuggestion = useCallback(
    () => ({
      char: '{',
      pluginKey: new PluginKey('mention {'),
      command: insertMentionWithoutTrailingSpace('comments'),
      allowSpaces: true,
      items: () => [], // No items - we show a form instead
      render: () => {
        let component: ReactRenderer | null = null;
        let popup: TippyInstance[] | null = null;

        return {
          onStart: (props: {
            editor: Editor;
            clientRect?: (() => DOMRect | null) | null;
            command: (item: { id: string; label: string }) => void;
          }) => {
            component = new ReactRenderer(CommentForm, {
              props,
              editor: props.editor,
            });

            if (!props.clientRect) return;

            popup = tippy('body', {
              getReferenceClientRect: props.clientRect as () => DOMRect,
              appendTo: () => document.body,
              content: component.element,
              showOnCreate: true,
              interactive: true,
              trigger: 'manual',
              placement: 'bottom-start',
            });
          },
          onUpdate: (props: { clientRect?: (() => DOMRect | null) | null }) => {
            component?.updateProps(props);
            if (!props.clientRect) return;
            popup?.[0]?.setProps({ getReferenceClientRect: props.clientRect as () => DOMRect });
          },
          onKeyDown: (props: { event: KeyboardEvent }) => {
            if (props.event.key === 'Escape') {
              popup?.[0]?.hide();
              return true;
            }
            return (
              (component?.ref as { onKeyDown?: (props: { event: KeyboardEvent }) => boolean })?.onKeyDown?.(props) ??
              false
            );
          },
          onExit: () => {
            popup?.[0]?.destroy();
            component?.destroy();
          },
        };
      },
    }),
    [],
  );

  const tiptapEditor = useEditor(
    {
      editable: !disabled && !readOnly,
      onCreate: () => setIsMounted(true),
      onFocus: () => onFocus?.(),
      onBlur: () => onBlur?.(),
      onUpdate: ({ editor }) => {
        const html = editor.getHTML() ?? '';
        lastHtmlRef.current = html;
        onChange?.(html, editor.getText() ?? '');
      },
      extensions: [
        StarterKit.configure({
          heading: false,
          blockquote: false,
          horizontalRule: false,
          ...(allowFormatting === true ? {} : { bold: false }),
          ...(allowFormatting === true ? {} : { bulletList: false }),
          ...(allowFormatting === true ? {} : { orderedList: false }),
          ...(allowFormatting === true ? {} : { strike: false }),
          ...(allowFormatting === true ? {} : { italic: false }),
        }),
        SubmitExtension(uniqFieldId, onSubmit),
        Placeholder.configure({ placeholder }),
        ...(allowLinks ? [Link.configure({ defaultProtocol: 'https', openOnClick: false })] : []),
        // @ Variables mention
        ...(!disableMentions && variables.length > 0
          ? [
              Mention.extend({ name: 'atVariables' }).configure({
                HTMLAttributes: { class: 'mention' },
                suggestion: createVariableSuggestion(variables),
                renderLabel,
              }),
            ]
          : []),
        // / Documents mention
        // Show document mention if documents exist OR onCreateDocument is provided
        ...(!disableMentions && (documents.length > 0 || onCreateDocument)
          ? [
              Mention.extend({ name: 'documents' }).configure({
                HTMLAttributes: { class: 'mention' },
                suggestion: createDocumentSuggestion(documents, onCreateDocument),
                renderLabel,
              }),
            ]
          : []),
        // { Comments mention - shows form for freeform text
        ...(!disableMentions && showComments
          ? [
              Mention.extend({ name: 'comments' }).configure({
                HTMLAttributes: { class: 'mention' },
                suggestion: createCommentSuggestion(),
                renderLabel,
              }),
            ]
          : []),
        // Idle trigger hint — small popup at cursor advertising @ / / / { when
        // the user stops typing in a focused, empty-selection editor.
        ...(() => {
          if (disableMentions || disabled || readOnly) return [];
          const triggers: TriggerInfo[] = [];
          if (variables.length > 0) triggers.push({ char: '@', label: 'Variable', tooltip: 'Insert a variable' });
          if (documents.length > 0 || onCreateDocument)
            triggers.push({ char: '/', label: 'Document', tooltip: 'Insert a document' });
          if (showComments) triggers.push({ char: '{', label: 'Comment', tooltip: 'Insert a comment' });
          if (triggers.length === 0) return [];
          return [createTriggerHintExtension(triggers)];
        })(),
      ],
      content: value,
    },
    [
      placeholder,
      uniqFieldId,
      allowLinks,
      allowFormatting,
      disabled,
      readOnly,
      disableMentions,
      JSON.stringify(variables),
      JSON.stringify(documents),
      showComments,
      // Use boolean to avoid recreating editor when callback reference changes
      !!onCreateDocument,
    ],
  );

  useImperativeHandle(ref, () => tiptapEditor!, [tiptapEditor]);

  const verticalPadMap: Record<Size, string> = {
    '3xs': 'py-0.5',
    '2xs': 'py-0.5',
    xs: 'py-1',
    sm: 'py-1.5',
    md: 'py-2',
    lg: 'py-2',
    xl: 'py-3',
    '2xl': 'py-4',
    '3xl': 'py-5',
    '4xl': 'py-6',
  };
  const verticalPad = verticalPadMap[size];

  const effectiveVariant: ColorVariant = composeVariant(color ?? 'gray', tone ?? 'filled');
  const [placeholderLight, placeholderDark] = getPlaceholderColors(effectiveVariant, !!error);

  // Size-dependent line height for paragraphs to match Input/Textarea
  const paragraphLineHeight: Record<Size, string> = {
    '3xs': '[&_p]:leading-3', // 12px
    '2xs': '[&_p]:leading-3.5', // 14px
    xs: '[&_p]:leading-4', // 16px
    sm: '[&_p]:leading-5', // 20px
    md: '[&_p]:leading-5', // 20px (text-sm line-height)
    lg: '[&_p]:leading-6', // 24px (text-base line-height)
    xl: '[&_p]:leading-6', // 24px
    '2xl': '[&_p]:leading-7', // 28px
    '3xl': '[&_p]:leading-8', // 32px
    '4xl': '[&_p]:leading-9', // 36px
  };

  // Numeric line height values for rows calculation (matches paragraphLineHeight)
  const lineHeightPx: Record<Size, number> = {
    '3xs': 12,
    '2xs': 14,
    xs: 16,
    sm: 20,
    md: 20,
    lg: 24,
    xl: 24,
    '2xl': 28,
    '3xl': 32,
    '4xl': 36,
  };
  const editorMinHeight = rows > 1 ? lineHeightPx[size] * rows : undefined;

  const editorWrapperClasses = cn(
    'flex flex-col',
    className,
    '[&_p]:m-0',
    paragraphLineHeight[size],
    '[&_.ProseMirror-focused]:outline-hidden',
    '[&_.tiptap]:outline-0 [&_.tiptap]:cursor-text [&_.tiptap]:min-h-full [&_.tiptap]:h-full',
    '[&_.tiptap]:overflow-auto [&_.tiptap]:wrap-break-word [&_.tiptap]:flex [&_.tiptap]:flex-col',
    '[&_.ProseMirror]:flex-1 [&_.ProseMirror]:min-h-0 [&_.ProseMirror]:grow',
    '[&_.is-editor-empty::before]:content-[attr(data-placeholder)]',
    '[&_.is-editor-empty::before]:float-left [&_.is-editor-empty::before]:h-0',
    '[&_.is-editor-empty::before]:pointer-events-none',
    '[&_.is-editor-empty::before]:text-(--tiptap-placeholder)',
    'dark:[&_.is-editor-empty::before]:text-(--tiptap-placeholder-dark)',
    '[&_.ProseMirror-separator]:opacity-0 [&_.ProseMirror-separator]:hidden',
    // Mention styling
    '[&_.mention]:p-px [&_.mention]:text-blue-600 [&_.mention]:rounded-xs',
    '[&_.mention]:underline [&_.mention]:decoration-2 [&_.mention]:decoration-blue-600/30',
    'dark:[&_.mention]:text-blue-400 dark:[&_.mention]:decoration-blue-400/30',
  );

  const containerClasses = cn(
    'flex flex-1 flex-col font-sans transition-colors overflow-hidden',
    sizeMinHeight(size),
    shapeRounded(shape, size),
    sizeFont(size),
    'border',
    inputVariantClasses(effectiveVariant, bordered, !!error, disabled, readOnly),
  );

  useEffect(() => {
    if (ref && typeof ref !== 'function') {
      ref.current = tiptapEditor;
    }
  }, [tiptapEditor, ref]);

  useEffect(() => {
    // Push an external `value` change into the editor, but never while the user
    // is actively editing — comparing against the editor's last-emitted HTML
    // (a ref) rather than a freshly serialized string, and not re-applying our
    // own keystrokes. `emitUpdate: false` keeps this programmatic set from
    // looping back through onUpdate. Without this guard a parent that normalizes
    // `value` (e.g. sanitization) would yank the caret mid-type.
    if (!tiptapEditor || tiptapEditor.isFocused) return;
    const next = value || '';
    const current = lastHtmlRef.current ?? tiptapEditor.getHTML();
    if (next !== current) {
      tiptapEditor.commands.setContent(next, false);
      lastHtmlRef.current = next;
    }
  }, [value, tiptapEditor]);

  useEffect(() => {
    if (autoFocus && isMounted && tiptapEditor && !wasFocused) {
      tiptapEditor.commands.focus();
      setWasFocused(true);
    }
  }, [tiptapEditor, isMounted, autoFocus, wasFocused]);

  return (
    <div
      className={editorWrapperClasses}
      style={
        {
          '--tiptap-placeholder': placeholderLight,
          '--tiptap-placeholder-dark': placeholderDark,
        } as React.CSSProperties
      }>
      <div style={style} className={containerClasses}>
        <div
          className={cn(
            'flex-1 flex flex-col min-h-0',
            verticalPad,
            sizePaddingL(size),
            sizePaddingR(size),
            editorClassName,
          )}
          style={editorMinHeight ? { minHeight: editorMinHeight } : undefined}>
          <EditorContent
            data-editor-id={uniqFieldId}
            autoFocus={autoFocus}
            editor={tiptapEditor}
            className="flex-1 h-full flex flex-col"
          />
        </div>
      </div>
      <input data-editor-id={uniqFieldId} type="submit" hidden />
      {error != null && (
        <div
          className={cn(
            'text-sm leading-5 font-normal m-0 text-fair-pink-600 dark:text-fair-pink-400 px-3 py-0.5',
            errorClassName,
          )}>
          {error}
        </div>
      )}
    </div>
  );
};

export default TipTapInput;
